import { useEffect, useMemo, useState } from "react";
import { MessageSquareShare, BellRing } from "lucide-react";
import { api, mapsUrl, toISODate } from "@/lib/api";
import { waLink } from "@/lib/utils2";

function buildMessage(name, appts) {
  const origin = window.location.origin;
  const day = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const lines = [`Buongiorno${name ? ` ${name}` : ""}! Ecco gli appuntamenti di oggi, ${day}:`, ""];
  appts.forEach((a, i) => {
    lines.push(`*${i + 1}) ore ${a.time || "da definire"} - ${a.client_name}*`);
    if (a.client_address) lines.push(`Indirizzo: ${a.client_address}`);
    if (a.floor) lines.push(`Piano: ${a.floor}`);
    if (a.client_phone) lines.push(`Telefono: ${a.client_phone}`);
    if (a.notes) lines.push(`Note: ${a.notes}`);
    const m = mapsUrl(a);
    if (m) lines.push(`Mappa: ${m}`);
    lines.push(`Scheda: ${origin}/appuntamenti/${a.id}`, "");
  });
  lines.push(`Per dubbi o perplessità accedi al gestionale: ${origin}`);
  return lines.join("\n");
}

export function WhatsAppDay({ appointments }) {
  const [staff, setStaff] = useState([]);
  const [sendTime, setSendTime] = useState("08:00");
  const [now, setNow] = useState(new Date());
  const today = toISODate(now);
  const sentKey = `wa_sent_${today}`;
  const [sentDay, setSentDay] = useState(() => (localStorage.getItem(sentKey) ? today : ""));
  const sent = sentDay === today || !!localStorage.getItem(sentKey);

  useEffect(() => {
    api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSendTime(data.whatsapp_time || "08:00")).catch(() => {});
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const todays = useMemo(() => appointments.filter((a) => a.agenda_date === today).sort((a, b) => (a.agenda_time || "99").localeCompare(b.agenda_time || "99")), [appointments, today]);
  const groups = useMemo(() => staff.map((s) => ({ ...s, appts: todays.filter((a) => a.assigned_to === s.id) })).filter((g) => g.appts.length), [staff, todays]);

  if (!todays.length) return <p className="text-sm text-[var(--muted)]" data-testid="whatsapp-day-empty">Nessun appuntamento oggi.</p>;
  const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const due = hhmm >= sendTime && !sent;
  const markSent = () => { localStorage.setItem(sentKey, "1"); setSentDay(today); };

  return (
    <div className={`rounded-2xl border p-4 sm:p-5 space-y-3 ${due ? "bg-emerald-50 border-emerald-300" : "bg-white border-[var(--border)]"}`} data-testid="whatsapp-day">
      <div className="flex items-start gap-2">
        {due ? <BellRing className="h-5 w-5 text-emerald-700 shrink-0" /> : <MessageSquareShare className="h-5 w-5 text-emerald-700 shrink-0" />}
        <div>
          <p className="font-semibold">{due ? "È l'ora di inviare il riepilogo di oggi su WhatsApp" : "Riepilogo di oggi su WhatsApp"}</p>
          <p className="text-xs text-[var(--muted)]">{todays.length} appuntamenti oggi · orario impostato {sendTime}{sent ? " · già inviato oggi" : ""}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {groups.map((g) => (
          <a key={g.id} href={waLink(g.phone, buildMessage(g.name, g.appts))} target="_blank" rel="noreferrer" onClick={markSent}
            className="btn-primary min-h-10 py-2 px-4 w-full !rounded-xl !bg-emerald-600 hover:!bg-emerald-700" data-testid={`whatsapp-send-${g.id}`}>
            Invia a {g.name} ({g.appts.length}){!g.phone && " – scegli contatto"}
          </a>
        ))}
        <a href={waLink("", buildMessage("", todays))} target="_blank" rel="noreferrer" onClick={markSent} className="btn-secondary min-h-10 py-2 px-4 w-full !rounded-xl" data-testid="whatsapp-send-all">
          Tutti gli appuntamenti – scegli contatto
        </a>
      </div>
    </div>
  );
}
